import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import tarefaService from '../../services/tarefaService';
import notificacaoNativaService from '../../services/NotificacaoNativaService';

interface TarefasPendentesPopupProps {
  // Props opcionais para controle externo
  forceCheck?: boolean;
  onClose?: () => void;
}

const TarefasPendentesPopup: React.FC<TarefasPendentesPopupProps> = ({ 
  forceCheck = false, 
  onClose 
}) => {
  const CHAVE_ULTIMA_VERIFICACAO = 'tarefasPendentesPopup_ultimaVerificacao';

  const { usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mostrarPopup, setMostrarPopup] = useState(false);
  const [tarefasPendentes, setTarefasPendentes] = useState(0);
  // Guardado em sessionStorage (não só em estado local) para que o "não
  // mostrar de novo por 5 minutos" sobreviva a uma remontagem do componente
  // (ex: reconexão de sessão) — sem isso, qualquer remount zerava o cooldown
  // e o popup reaparecia mesmo tendo acabado de ser dispensado.
  const [ultimaVerificacao, setUltimaVerificacaoState] = useState<number>(
    () => Number(sessionStorage.getItem(CHAVE_ULTIMA_VERIFICACAO)) || 0
  );
  const setUltimaVerificacao = (valor: number) => {
    sessionStorage.setItem(CHAVE_ULTIMA_VERIFICACAO, String(valor));
    setUltimaVerificacaoState(valor);
  };
  
  // Referência para controlar o debounce
  const emVerificacao = useRef(false);
  const ultimaChamada = useRef(0);

  // Função para verificar tarefas pendentes com debounce
  const verificarTarefasPendentes = async () => {
    if (!usuario) return;
    
    // Implementar debounce para evitar chamadas frequentes
    const agora = Date.now();
    const tempoMinimoDebouce = 2000; // 2 segundos de debounce
    
    // Se já estiver em verificação ou se a última chamada foi há menos de 2 segundos, não fazer nova chamada
    if (emVerificacao.current || (agora - ultimaChamada.current < tempoMinimoDebouce)) {
      console.log('Popup - Verificação ignorada (debounce)');
      return;
    }
    
    // Marcar início da verificação
    emVerificacao.current = true;
    ultimaChamada.current = agora;

    try {
      console.log('Popup - Verificando tarefas pendentes...');
      const contador = await tarefaService.contarTarefasPendentes();
      
      console.log(`Popup - Encontradas ${contador} tarefas pendentes`);
      setTarefasPendentes(contador);

      // Mostrar popup se há tarefas pendentes
      const agoraPopup = Date.now();
      const tempoMinimo = 5 * 60 * 1000; // 5 minutos entre popups
      const primeiraVerificacao = ultimaVerificacao === 0;

      // Mostrar popup se:
      // 1. Há tarefas pendentes
      // 2. Não está sendo mostrado já
      // 3. É a primeira verificação (ao carregar) OU passou tempo suficiente
      if (contador > 0 && !mostrarPopup && (primeiraVerificacao || agoraPopup - ultimaVerificacao > tempoMinimo)) {
        console.log(`Popup - Mostrando popup para ${contador} tarefas pendentes`);
        setMostrarPopup(true);
        setUltimaVerificacao(agoraPopup);
        
        // ENVIAR NOTIFICAÇÃO NATIVA DO NAVEGADOR
        console.log('Popup - Enviando notificação nativa do Windows');
        notificacaoNativaService.notificarTarefasPendentes(contador).then(enviada => {
          if (enviada) {
            console.log('Notificação nativa enviada com sucesso');
          } else {
            console.log('Notificação nativa não foi enviada (página visível ou sem permissão)');
          }
        });
      }
    } catch (error) {
      console.error('Erro ao verificar tarefas pendentes:', error);
    } finally {
      // Marcar fim da verificação para liberar o debounce
      emVerificacao.current = false;
    }
  };

  // Verificar tarefas pendentes periodicamente
  useEffect(() => {
    if (!usuario) return;

    // Verificar se veio de um link de email (sessionStorage)
    const vemDeEmail = sessionStorage.getItem('emailRedirect');
    if (vemDeEmail) {
      console.log('TarefasPendentesPopup - Redirecionamento de email detectado, não mostrar popup');
      sessionStorage.removeItem('emailRedirect');
      return;
    }

    // Verificação IMEDIATA ao carregar (assim que usuário fizer login)
    console.log('TarefasPendentesPopup - Verificação inicial ao carregar');
    verificarTarefasPendentes();

    // Configurar intervalo para verificações periódicas
    const interval = setInterval(() => {
      verificarTarefasPendentes();
    }, 10 * 60 * 1000); // 10 minutos

    // Limpeza ao desmontar
    return () => {
      console.log('TarefasPendentesPopup - Limpando intervalo');
      clearInterval(interval);
    };
  }, [usuario]); // Executa quando usuário faz login

  // Função para fechar o popup
  const fecharPopup = () => {
    setMostrarPopup(false);
    if (onClose) onClose();
  };

  // Função para ir para tarefas recebidas
  const irParaTarefasRecebidas = () => {
    navigate('/kanban/recebidas');
    fecharPopup();
  };

  // Função para dispensar por um tempo
  const dispensarTemporariamente = () => {
    setUltimaVerificacao(Date.now());
    fecharPopup();
  };

  if (!mostrarPopup || tarefasPendentes === 0) {
    return null;
  }

  return (
    <>
      {/* Overlay escuro */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={fecharPopup}
      >
        {/* Popup */}
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100 animate-pulse-once"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-orange-500 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 mr-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
              <h3 className="text-lg font-bold">Tarefas Pendentes!</h3>
            </div>
            <button 
              onClick={fecharPopup}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">
                  {tarefasPendentes > 99 ? '99+' : tarefasPendentes}
                </span>
              </div>
              <p className="text-gray-700 text-lg">
                Você tem <strong>{tarefasPendentes}</strong> {tarefasPendentes === 1 ? 'tarefa pendente' : 'tarefas pendentes'} aguardando sua atenção.
              </p>
            </div>

            {/* Botões */}
            <div className="space-y-3">
              <button
                onClick={irParaTarefasRecebidas}
                className="w-full bg-bravo-primary hover:bg-bravo-primary-dark text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5 mr-2" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                  />
                </svg>
                Ver Tarefas Recebidas
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={dispensarTemporariamente}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Lembrar depois
                </button>
                <button
                  onClick={fecharPopup}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos para animação */}
      <style>{`
        @keyframes pulse-once {
          0% { transform: scale(0.95); opacity: 0; }
          50% { transform: scale(1.02); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-pulse-once {
          animation: pulse-once 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default TarefasPendentesPopup;
